/**
 * ZAXIA AskZaxiaView Component
 * Phase 4A + 4B: Voice-First Concierge with Contextual Recommendations
 *
 * Features:
 * - Push-to-talk: Hold Space to speak
 * - Natural TTS via centralized ZaxiaVoiceService
 * - Slot-based restaurant booking (time, party-size from single utterance)
 * - Voice Command Contract v1 (calendar, bill, weather, TV)
 * - Time-window based greeting (once per morning/afternoon/evening)
 * - Proper guest identity (plural guests supported)
 * - Booking persistence to backend calendar
 * - Phase 4B: Session memory for recommendations
 * - Phase 4B: "Book the restaurant", "the first one" voice actions
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  askConcierge,
  ConciergeIntent,
  ConciergeResponse,
  Recommendation,
  RecommendationAction,
  RecommendationType,
  ApiGuest,
  CreateBookingRequest,
} from '@/api';
import { zaxiaVoice, createSpeechRecognition, isSpeechRecognitionAvailable, type SpeechRecognitionEvent } from '@/services';
import { useGuestContext } from '@/context';

// =============================================================================
// TYPES
// =============================================================================

type ConciergeState =
  | 'idle'           // Waiting for voice input
  | 'listening'      // Actively recording speech
  | 'processing'     // Processing voice / thinking
  | 'speaking'       // Zaxia is speaking response
  | 'awaiting-reply';// Waiting for user reply in booking flow

interface BookingFlowState {
  active: boolean;
  step: 'initial' | 'time' | 'party-size' | 'review' | 'edit-choice' | 'confirm' | 'complete';
  venue?: Recommendation;
  time?: string;       // 24h format "HH:MM"
  partySize?: number;
}

// Phase 4B: Session memory for contextual follow-ups
interface SessionMemory {
  lastRecommendations: Recommendation[];
  lastTopic: ConciergeIntent | null;
  awaitingRecommendationAction: boolean; // Waiting for "yes", "book the restaurant", etc.
}

// Voice command actions
type VoiceAction =
  | { type: 'OPEN_CALENDAR' }
  | { type: 'OPEN_BILL' }
  | { type: 'OPEN_MESSAGES' }                            // Phase D1.2: Messages view
  | { type: 'HOME' }                                      // Phase D1.2: Return to grid
  | { type: 'CLOSE' }                                     // Phase D1.2: Close modal/view
  | { type: 'WEATHER' }
  | { type: 'ZONE_SIDEBAR' }                              // Phase D1.2: Switch to sidebar
  | { type: 'ZONE_CONCIERGE' }                            // Phase D1.2: Switch to concierge
  | { type: 'ZONE_TV' }                                   // Phase D1.2: Switch to TV
  | { type: 'TV_CHANNEL'; channel: string }
  | { type: 'TV_FULLSCREEN' }
  | { type: 'TV_EXIT_FULLSCREEN' }                        // Phase D1.2: Exit TV fullscreen
  | { type: 'TV_NEXT_CHANNEL' }                           // Phase D1.2: Next channel
  | { type: 'TV_PREV_CHANNEL' }                           // Phase D1.2: Previous channel
  | { type: 'TV_MUTE' }                                   // Phase D2.2: Mute TV
  | { type: 'TV_UNMUTE' }                                 // Phase D2.2: Unmute TV
  | { type: 'TV_ON' }                                     // Phase D2.2: Turn TV on
  | { type: 'TV_OFF' }                                    // Phase D2.2: Turn TV off
  | { type: 'SHOW_PROMOTIONS' }                           // Phase D2.3: Show promotions
  | { type: 'BOOK_RESTAURANT'; slots: { time?: string; partySize?: number; venue?: string } }
  | { type: 'CONCIERGE_INTENT'; intent: ConciergeIntent }
  | { type: 'CANCEL_BOOKING' }
  | { type: 'EXIT' }
  | { type: 'BOOK_RECOMMENDATION'; index: number }       // Phase 4B: "the first one", "book the restaurant"
  | { type: 'SOMETHING_ELSE' }                           // Phase 4B: "something else"
  | { type: 'ROOM_SERVICE' }                             // Phase D2.4: Room service ordering
  | { type: 'SPA' }                                      // Phase D2.5: Spa booking
  | { type: 'EXPLORE' }                                  // Phase D3.1: Explore nearby
  | { type: 'GREEN_STAY_ASK' }                           // Phase D4.1: Ask about Green Stay
  | { type: 'GREEN_STAY_ENABLE' }                        // Phase D4.1: Enable Green Stay
  | { type: 'GREEN_STAY_DISABLE' }                       // Phase D4.1: Disable Green Stay
  | { type: 'GREEN_STAY_SKIP_CLEANING'; day: string }    // Phase D4.2: Skip cleaning on specific day
  | { type: 'GREEN_STAY_TOWELS_ONLY'; day: string }      // Phase D4.2: Towels only on specific day
  | { type: 'GREEN_STAY_CLEAN_ROOM'; day: string }       // Phase D4.2: Clean room on specific day (remove from skip)
  | { type: 'GREEN_STAY_SHOW' }                          // Phase D4.2: Show current green preferences
  | { type: 'GREEN_STAY_CLEAR' }                         // Phase D4.2: Clear all green preferences
  | { type: 'TRANSPORT_REQUEST'; destination?: string }  // Phase D4.3: Transport/taxi request
  | { type: 'HOUSEKEEPING_REQUEST' }                     // Phase D4.3: Extra cleaning request
  | { type: 'LAUNDRY_REQUEST' };                         // Phase D4.3: Laundry request

// Greeting time windows
type TimeWindow = 'morning' | 'afternoon' | 'evening';

// =============================================================================
// CONSTANTS
// =============================================================================

const TYPE_ICONS: Record<RecommendationType, string> = {
  [RecommendationType.VENUE]: '🍽️',
  [RecommendationType.SPA]: '💆',
  [RecommendationType.ACTIVITY]: '🎯',
  [RecommendationType.LOCAL]: '📍',
};

// Voice Command Contract v1 - Limited commands
const VOICE_COMMANDS: Array<{ pattern: RegExp; action: VoiceAction }> = [
  // Phase D4.1: Green Stay - MUST come before generic yes/no patterns
  { pattern: /\b(what('?s| is)\s*green\s*stay|tell\s*me\s*(about|more\s*about)?\s*green\s*stay|explain\s*green\s*stay)\b/i, action: { type: 'GREEN_STAY_ASK' } },
  { pattern: /\b(enable\s*green\s*stay|turn\s*on\s*green\s*stay|activate\s*green\s*stay|start\s*green\s*stay|green\s*stay\s*(on|enable|activate))\b/i, action: { type: 'GREEN_STAY_ENABLE' } },
  { pattern: /\b(disable\s*green\s*stay|turn\s*off\s*green\s*stay|deactivate\s*green\s*stay|stop\s*green\s*stay|green\s*stay\s*(off|disable))\b/i, action: { type: 'GREEN_STAY_DISABLE' } },
  // Phase D4.2: Green Stay scheduled preferences - MUST come after enable/disable
  { pattern: /\b(show|what('?s| are))?\s*(my\s*)?green\s*(stay\s*)?(preferences?|settings?|schedule)\b/i, action: { type: 'GREEN_STAY_SHOW' } },
  { pattern: /\b(clear|reset|remove)\s*(all\s*)?(my\s*)?green\s*(stay\s*)?(preferences?|settings?|schedule)\b/i, action: { type: 'GREEN_STAY_CLEAR' } },
  // Cancel booking (checked first in booking flow)
  { pattern: /\b(cancel|stop|abort|forget it|never\s*mind)\b/i, action: { type: 'CANCEL_BOOKING' } },
  // Phase D2.4: Room service - MUST come before restaurant/food patterns
  { pattern: /\b(room\s*service|order\s*room\s*service|in[\s-]?room\s*dining)\b/i, action: { type: 'ROOM_SERVICE' } },
  // Phase D2.5: Spa - MUST come before generic intent patterns
  { pattern: /\b(open\s*(the\s*)?spa|go\s*to\s*(the\s*)?spa|spa\s*(page|menu|booking))\b/i, action: { type: 'SPA' } },
  { pattern: /\b(book\s*(a\s*)?(spa|massage)|i\s*want\s*(a\s*)?(spa|massage)|get\s*(a\s*)?(spa|massage))\b/i, action: { type: 'SPA' } },
  { pattern: /\b(spa\s*treatment|massage\s*treatment|book\s*treatment)\b/i, action: { type: 'SPA' } },
  // Phase D3.1: Explore nearby - MUST come before generic patterns
  { pattern: /^explore$/i, action: { type: 'EXPLORE' } },  // Just "explore" alone
  { pattern: /\b(open\s*(the\s*)?explore|go\s*to\s*(the\s*)?explore|explore\s*(page|view|screen))\b/i, action: { type: 'EXPLORE' } },
  { pattern: /\b(explore\s*nearby|explore\s*around|nearby\s*places|places\s*nearby)\b/i, action: { type: 'EXPLORE' } },
  { pattern: /\b(show\s*(me\s*)?(places|things)\s*(around|near)\s*(the\s*)?hotel)\b/i, action: { type: 'EXPLORE' } },
  { pattern: /\b(what('?s| is)\s*(around|near)\s*(the\s*)?hotel)\b/i, action: { type: 'EXPLORE' } },
  { pattern: /\b(things\s*to\s*do\s*(around|nearby))\b/i, action: { type: 'EXPLORE' } },
  // Phase D1.2: Exit TV fullscreen - MUST come before generic "exit"
  { pattern: /\b(exit\s*full\s*screen|exit\s*fullscreen|minimize\s*tv|normal\s*screen|leave\s*full\s*screen)\b/i, action: { type: 'TV_EXIT_FULLSCREEN' } },
  // Phase D2.2: Mute/Unmute TV - MUST come before generic exit
  { pattern: /\b(mute|mute\s*tv|mute\s*the\s*tv|sound\s*off|turn\s*off\s*sound)\b/i, action: { type: 'TV_MUTE' } },
  { pattern: /\b(unmute|unmute\s*tv|unmute\s*the\s*tv|sound\s*on|turn\s*on\s*sound|volume\s*on)\b/i, action: { type: 'TV_UNMUTE' } },
  // Phase D2.2: TV ON/OFF - MUST come before generic exit
  { pattern: /\b(tv\s*on|turn\s*on\s*(the\s*)?tv|switch\s*on\s*(the\s*)?tv|power\s*on\s*(the\s*)?tv)\b/i, action: { type: 'TV_ON' } },
  { pattern: /\b(tv\s*off|turn\s*off\s*(the\s*)?tv|switch\s*off\s*(the\s*)?tv|power\s*off\s*(the\s*)?tv)\b/i, action: { type: 'TV_OFF' } },
  // Phase D2.3: Show promotions
  { pattern: /\b(show\s*(today'?s?|me\s*(the)?|the)?\s*(offers?|promotions?|deals?|specials?))\b/i, action: { type: 'SHOW_PROMOTIONS' } },
  { pattern: /\b(what('?s| are)\s*(today'?s?|the)?\s*(offers?|promotions?|deals?|specials?))\b/i, action: { type: 'SHOW_PROMOTIONS' } },
  // Exit Ask Zaxia (including polite declines after "anything else?")
  { pattern: /\b(exit|close|go\s*back|leave|goodbye|bye)\b/i, action: { type: 'EXIT' } },
  { pattern: /^\s*(no|nope)\s*(thanks?|thank\s*you)?\s*\.?\s*$/i, action: { type: 'EXIT' } },
  { pattern: /\b(that'?s?\s*(all|it)|nothing\s*(else)?|i'?m\s*(good|fine|all\s*set))\b/i, action: { type: 'EXIT' } },
  // Phase D1.2: Zone switching
  { pattern: /\b(sidebar|side\s*bar|side\s*menu|left\s*(menu|panel))\b/i, action: { type: 'ZONE_SIDEBAR' } },
  { pattern: /\b(concierge|main|main\s*(menu|screen|area))\b/i, action: { type: 'ZONE_CONCIERGE' } },
  { pattern: /\b(tv|television|watch\s*tv|tv\s*(mode|section))\b/i, action: { type: 'ZONE_TV' } },
  // Phase D1.2: Home / Return to grid
  { pattern: /\b(home|go\s*home|back\s*to\s*(home|grid))\b/i, action: { type: 'HOME' } },
  // Phase D1.2: Close modal/view
  { pattern: /\bclose\s*(this|it|the\s*(modal|window|view))?\b/i, action: { type: 'CLOSE' } },
  // Calendar
  { pattern: /open\s*(my)?\s*calendar|show\s*(my)?\s*calendar|calendar/i, action: { type: 'OPEN_CALENDAR' } },
  // Phase D1.2: Messages
  { pattern: /open\s*(my)?\s*messages?|show\s*(my)?\s*messages?|messages?/i, action: { type: 'OPEN_MESSAGES' } },
  // Bill
  { pattern: /open\s*(my)?\s*bill|show\s*(my)?\s*bill|my\s*bill|view\s*bill/i, action: { type: 'OPEN_BILL' } },
  // Weather
  { pattern: /weather|how.?s the weather|forecast/i, action: { type: 'WEATHER' } },
  // Phase D1.2: TV Channels (expanded)
  { pattern: /switch\s*to\s*(hbo)/i, action: { type: 'TV_CHANNEL', channel: 'hbo' } },
  { pattern: /switch\s*to\s*(cnn)/i, action: { type: 'TV_CHANNEL', channel: 'cnn' } },
  { pattern: /switch\s*to\s*(bbc)/i, action: { type: 'TV_CHANNEL', channel: 'bbc' } },
  { pattern: /switch\s*to\s*(espn)/i, action: { type: 'TV_CHANNEL', channel: 'espn' } },
  { pattern: /switch\s*to\s*(discovery)/i, action: { type: 'TV_CHANNEL', channel: 'discovery' } },
  { pattern: /switch\s*to\s*(streaming)/i, action: { type: 'TV_CHANNEL', channel: 'streaming' } },
  { pattern: /put\s*on\s*(hbo|cnn|bbc|espn|discovery|streaming)/i, action: { type: 'TV_CHANNEL', channel: '$1' } },
  { pattern: /channel\s*(hbo|cnn|bbc|espn|discovery|streaming)/i, action: { type: 'TV_CHANNEL', channel: '$1' } },
  // Phase D1.2: TV Next/Previous channel
  { pattern: /\b(next\s*channel|channel\s*up)\b/i, action: { type: 'TV_NEXT_CHANNEL' } },
  { pattern: /\b(previous\s*channel|last\s*channel|channel\s*down)\b/i, action: { type: 'TV_PREV_CHANNEL' } },
  // Phase D1.2: TV Fullscreen
  { pattern: /\b(full\s*screen|fullscreen|maximize\s*tv|tv\s*full\s*screen)\b/i, action: { type: 'TV_FULLSCREEN' } },
  // Phase D4.3: Transport/Taxi requests
  { pattern: /\b(get\s*me\s*(a\s*)?(taxi|cab|uber|car)|call\s*(a\s*)?(taxi|cab)|order\s*(a\s*)?(taxi|cab)|i\s*need\s*(a\s*)?(taxi|cab|ride))\b/i, action: { type: 'TRANSPORT_REQUEST' } },
  { pattern: /\b(book\s*(a\s*)?(taxi|cab|transport)|taxi\s*to|cab\s*to|transport\s*to)\b/i, action: { type: 'TRANSPORT_REQUEST' } },
  { pattern: /\b(how\s*(do\s*i|can\s*i)\s*get\s*to|take\s*me\s*to|go\s*to\s*the\s*(airport|station|downtown))\b/i, action: { type: 'TRANSPORT_REQUEST' } },
  // Phase D4.3: Housekeeping/Cleaning requests
  { pattern: /\b(clean\s*(my\s*)?room|extra\s*cleaning|housekeeping|make\s*(up\s*)?(the\s*)?room)\b/i, action: { type: 'HOUSEKEEPING_REQUEST' } },
  { pattern: /\b(i\s*need\s*(a\s*)?clean|room\s*(needs?\s*)?(cleaning|service))\b/i, action: { type: 'HOUSEKEEPING_REQUEST' } },
  { pattern: /\b(send\s*housekeeping|request\s*housekeeping)\b/i, action: { type: 'HOUSEKEEPING_REQUEST' } },
  // Phase D4.3: Laundry requests
  { pattern: /\b(laundry|wash\s*(my\s*)?(clothes|shirts?|pants?)|dry\s*clean(ing)?)\b/i, action: { type: 'LAUNDRY_REQUEST' } },
  { pattern: /\b(i\s*need\s*(my\s*)?(clothes|laundry)\s*(washed|cleaned|pressed))\b/i, action: { type: 'LAUNDRY_REQUEST' } },
  { pattern: /\b(iron(ing)?\s*(service)?|press\s*(my\s*)?(clothes|shirts?))\b/i, action: { type: 'LAUNDRY_REQUEST' } },
];

// Intent matching patterns (for concierge recommendations)
const INTENT_PATTERNS: Array<{ pattern: RegExp; intent: ConciergeIntent }> = [
  { pattern: /what\s+(should|can)\s+i\s+do\s+today|today|day\s*plan/i, intent: ConciergeIntent.WHAT_SHOULD_I_DO_TODAY },
  { pattern: /relax|spa|massage|wellness|chill|unwind|calm/i, intent: ConciergeIntent.I_WANT_TO_RELAX },
  { pattern: /tonight|evening|night\s*life|happening\s+tonight/i, intent: ConciergeIntent.WHATS_HAPPENING_TONIGHT },
  { pattern: /recommend|suggestion|what\s+do\s+you\s+suggest|ideas/i, intent: ConciergeIntent.RECOMMENDATIONS },
];

// Restaurant booking patterns (slot extraction)
const RESTAURANT_PATTERNS = [
  /book\s*(me\s*)?(a\s*)?table/i,
  /hungry|food|eat|meal|restaurant|dinner|lunch|breakfast/i,
  /reserve\s*(a\s*)?table/i,
  /make\s*(a\s*)?reservation/i,
];

// Time parsing patterns
const TIME_PATTERNS: Array<{ pattern: RegExp; parse: (m: RegExpMatchArray) => string }> = [
  // "8:00 pm", "8:30 p.m." - 12h time with colon
  { pattern: /\b(\d{1,2}):(\d{2})\s*(pm|p\.?m\.?)\b/i, parse: (m) => {
    const h = parseInt(m[1]!);
    return `${h < 12 ? h + 12 : h}:${m[2]}`;
  }},
  { pattern: /\b(\d{1,2}):(\d{2})\s*(am|a\.?m\.?)\b/i, parse: (m) => `${m[1]!.padStart(2, '0')}:${m[2]}` },
  // "8pm", "8 pm", "8 p.m." - without colon (must come AFTER colon patterns)
  { pattern: /\b(\d{1,2})\s*(pm|p\.?m\.?)\b/i, parse: (m) => {
    const h = parseInt(m[1]!);
    return `${h < 12 ? h + 12 : h}:00`;
  }},
  // "8am", "8 am"
  { pattern: /\b(\d{1,2})\s*(am|a\.?m\.?)\b/i, parse: (m) => `${m[1]!.padStart(2, '0')}:00` },
  // "19:00", "20:30" - 24h time (no am/pm)
  { pattern: /\b(\d{1,2}):(\d{2})\b/i, parse: (m) => `${m[1]!.padStart(2, '0')}:${m[2]}` },
  // Words - evening times default to PM
  { pattern: /\b(seven|7)\s*(o.?clock)?(\s*(in the\s*)?(evening|tonight|pm))?\b/i, parse: () => '19:00' },
  { pattern: /\b(eight|8)\s*(o.?clock)?(\s*(in the\s*)?(evening|tonight|pm))?\b/i, parse: () => '20:00' },
  { pattern: /\b(nine|9)\s*(o.?clock)?(\s*(in the\s*)?(evening|tonight|pm))?\b/i, parse: () => '21:00' },
  { pattern: /\bhalf\s*(past)?\s*(seven|7)\b/i, parse: () => '19:30' },
  { pattern: /\bhalf\s*(past)?\s*(eight|8)\b/i, parse: () => '20:30' },
  { pattern: /\btonight\b/i, parse: () => '20:00' }, // Default tonight to 8pm
];

// Party size patterns
const PARTY_SIZE_PATTERNS = [
  { pattern: /\bjust\s*(me|myself)\b|for\s*one\b|\b(one|1)\s*(person|guest|people)?\b/i, size: 1 },
  { pattern: /\b(two|2)\s*(people|persons|guests)?\b|couple|just\s*(us|the two of us)\b|for\s*two\b/i, size: 2 },
  { pattern: /\b(three|3)\s*(people|persons|guests)?\b|for\s*three\b/i, size: 3 },
  { pattern: /\b(four|4)\s*(people|persons|guests)?\b|for\s*four\b/i, size: 4 },
  { pattern: /\b(five|5)\s*(people|persons|guests)?\b|for\s*five\b/i, size: 5 },
  { pattern: /\b(six|6)\s*(people|persons|guests)?\b|for\s*six\b/i, size: 6 },
];

// Confirmation patterns
const CONFIRM_PATTERNS = [
  { pattern: /\b(yes|yeah|yep|sure|confirm|perfect|great|sounds\s+good|book\s+it|please|absolutely|do\s+it|correct|right)\b/i, confirmed: true },
  { pattern: /\b(no|nope|change|different|wait|wrong|not\s+right)\b/i, confirmed: false },
];

// =============================================================================
// HELPERS
// =============================================================================

function getCurrentTimeWindow(): TimeWindow {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function getGreeting(timeWindow: TimeWindow): string {
  switch (timeWindow) {
    case 'morning': return 'Good morning';
    case 'afternoon': return 'Good afternoon';
    case 'evening': return 'Good evening';
  }
}

function getGuestDisplayName(guest: ApiGuest | null | undefined): string {
  if (!guest) return 'there';
  return guest.firstName;
}

function parseVoiceCommand(transcript: string): VoiceAction | null {
  const lower = transcript.toLowerCase();

  // Check voice commands first
  for (const { pattern, action } of VOICE_COMMANDS) {
    const match = lower.match(pattern);
    if (match) {
      // Handle dynamic channel extraction
      if (action.type === 'TV_CHANNEL' && match[1]) {
        return { type: 'TV_CHANNEL', channel: match[1] };
      }
      return action;
    }
  }

  // Phase D4.2: Check for scheduled Green Stay commands with day extraction
  // Skip cleaning patterns: "skip cleaning tomorrow", "skip cleaning on monday", "no cleaning on the 15th"
  const skipCleaningMatch = lower.match(/\b(skip|no)\s*(the\s*)?(room\s*)?(cleaning|housekeeping)\s*(on\s*)?(the\s*)?(\w+|[\d]+(?:st|nd|rd|th)?)\b/i);
  if (skipCleaningMatch && skipCleaningMatch[7]) {
    return { type: 'GREEN_STAY_SKIP_CLEANING', day: skipCleaningMatch[7] };
  }

  // Towels only patterns: "only towels tomorrow", "towels only on thursday", "just towels on friday"
  const towelsOnlyMatch = lower.match(/\b(only|just)\s*(fresh\s*)?(towels?)\s*(on\s*)?(the\s*)?(\w+|[\d]+(?:st|nd|rd|th)?)\b/i);
  if (towelsOnlyMatch && towelsOnlyMatch[6]) {
    return { type: 'GREEN_STAY_TOWELS_ONLY', day: towelsOnlyMatch[6] };
  }
  // Alternative: "towels only tomorrow"
  const towelsOnlyAlt = lower.match(/\b(towels?)\s*(only|just)\s*(on\s*)?(the\s*)?(\w+|[\d]+(?:st|nd|rd|th)?)\b/i);
  if (towelsOnlyAlt && towelsOnlyAlt[5]) {
    return { type: 'GREEN_STAY_TOWELS_ONLY', day: towelsOnlyAlt[5] };
  }

  // Clean room patterns: "clean my room tomorrow", "clean the room on monday", "i want cleaning tomorrow"
  const cleanRoomMatch = lower.match(/\b(clean|cleaning)\s*(the\s*|my\s*)?(room\s*)?(on\s*)?(the\s*)?(\w+|[\d]+(?:st|nd|rd|th)?)\b/i);
  if (cleanRoomMatch && cleanRoomMatch[6]) {
    return { type: 'GREEN_STAY_CLEAN_ROOM', day: cleanRoomMatch[6] };
  }
  // Alternative: "i want a full clean tomorrow"
  const fullCleanMatch = lower.match(/\b(full|regular|normal)\s*(clean|cleaning)\s*(on\s*)?(the\s*)?(\w+|[\d]+(?:st|nd|rd|th)?)\b/i);
  if (fullCleanMatch && fullCleanMatch[5]) {
    return { type: 'GREEN_STAY_CLEAN_ROOM', day: fullCleanMatch[5] };
  }

  // Check for restaurant booking with slot extraction
  for (const pattern of RESTAURANT_PATTERNS) {
    if (pattern.test(lower)) {
      const time = parseTime(transcript);
      const partySize = parsePartySize(transcript);
      return {
        type: 'BOOK_RESTAURANT',
        slots: { time: time || undefined, partySize: partySize || undefined }
      };
    }
  }

  // Check concierge intents
  for (const { pattern, intent } of INTENT_PATTERNS) {
    if (pattern.test(lower)) {
      return { type: 'CONCIERGE_INTENT', intent };
    }
  }

  return null;
}

function parseTime(transcript: string): string | null {
  const lower = transcript.toLowerCase();
  for (const { pattern, parse } of TIME_PATTERNS) {
    const match = lower.match(pattern);
    if (match) {
      return parse(match);
    }
  }
  return null;
}

function parsePartySize(transcript: string): number | null {
  const lower = transcript.toLowerCase();
  for (const { pattern, size } of PARTY_SIZE_PATTERNS) {
    if (pattern.test(lower)) {
      return size;
    }
  }
  return null;
}

function parseConfirmation(transcript: string): boolean | null {
  const lower = transcript.toLowerCase();
  for (const { pattern, confirmed } of CONFIRM_PATTERNS) {
    if (pattern.test(lower)) {
      return confirmed;
    }
  }
  return null;
}

function formatTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const h = hours! % 12 || 12;
  const ampm = hours! >= 12 ? 'PM' : 'AM';
  return minutes ? `${h}:${minutes.toString().padStart(2, '0')} ${ampm}` : `${h} ${ampm}`;
}

/**
 * Phase 4B: Parse recommendation actions from transcript
 * Returns the index (0-based) of the recommendation to book, or -1 if not found
 */
function parseRecommendationAction(transcript: string, recommendations: Recommendation[]): number {
  const lower = transcript.toLowerCase();

  // Check for ordinal selection: "the first one", "second one", "number one"
  const ordinalPatterns = [
    { pattern: /\b(first|1st|number\s*one|one)\b/i, index: 0 },
    { pattern: /\b(second|2nd|number\s*two|two)\b/i, index: 1 },
    { pattern: /\b(third|3rd|number\s*three|three)\b/i, index: 2 },
    { pattern: /\b(fourth|4th|number\s*four|four)\b/i, index: 3 },
    { pattern: /\b(fifth|5th|number\s*five|five)\b/i, index: 4 },
  ];

  for (const { pattern, index } of ordinalPatterns) {
    if (pattern.test(lower) && index < recommendations.length) {
      return index;
    }
  }

  // Check for specific recommendation by name/type: "book the restaurant", "the spa", "book terra"
  for (let i = 0; i < recommendations.length; i++) {
    const rec = recommendations[i]!;
    const title = rec.title.toLowerCase();
    const type = rec.type.toLowerCase();

    // Match by title (e.g., "book terra", "terra")
    if (lower.includes(title) || title.split(' ').some(word => word.length > 3 && lower.includes(word))) {
      return i;
    }

    // Match by type (e.g., "the restaurant", "book the spa", "the massage")
    if (type === 'venue' && /\b(restaurant|dinner|lunch|breakfast|bar|terrace)\b/i.test(lower)) {
      return i;
    }
    if (type === 'spa' && /\b(spa|massage|treatment|wellness)\b/i.test(lower)) {
      return i;
    }
    if (type === 'activity' && /\b(activity|pool|gym|fitness|yoga)\b/i.test(lower)) {
      return i;
    }
  }

  return -1;
}

/**
 * Phase 4B: Check if user wants something else
 */
function wantsSomethingElse(transcript: string): boolean {
  return /\b(something\s*else|different|other|another|no\s*thanks?|not\s*interested)\b/i.test(transcript.toLowerCase());
}

// =============================================================================
// Phase D4.2: Scheduled Green Stay Helpers
// =============================================================================

const DAY_NAME_MAP: Record<string, number> = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
};

/**
 * Parse a day reference from voice input (e.g., "tomorrow", "Monday", "the 15th")
 * Returns an ISO date string or null if parsing fails
 */
function parseDayReference(dayRef: string, _stayCheckIn?: string, _stayCheckOut?: string): string | null {
  const today = new Date();
  const ref = dayRef.toLowerCase().trim();
  
  // "today"
  if (ref === 'today') {
    return today.toISOString().split('T')[0]!;
  }
  
  // "tomorrow"
  if (ref === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0]!;
  }
  
  // Day of week (Monday, Tuesday, etc.)
  if (ref in DAY_NAME_MAP) {
    const targetDay = DAY_NAME_MAP[ref]!;
    const currentDay = today.getDay();
    let daysAhead = targetDay - currentDay;
    if (daysAhead <= 0) daysAhead += 7; // Next week if today or past
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysAhead);
    return targetDate.toISOString().split('T')[0]!;
  }
  
  // Date number like "the 15th" or just "15"
  const dateMatch = ref.match(/(\d{1,2})(st|nd|rd|th)?/);
  if (dateMatch) {
    const dayNum = parseInt(dateMatch[1]!, 10);
    if (dayNum >= 1 && dayNum <= 31) {
      // Use current month, or next month if day has passed
      const targetDate = new Date(today.getFullYear(), today.getMonth(), dayNum);
      if (targetDate < today) {
        targetDate.setMonth(targetDate.getMonth() + 1);
      }
      return targetDate.toISOString().split('T')[0]!;
    }
  }
  
  return null;
}

/**
 * Check if a date is within the guest's stay range
 */
function isDateInStayRange(dateStr: string, stayCheckIn?: string, stayCheckOut?: string): boolean {
  if (!stayCheckIn || !stayCheckOut) return true; // If no stay range, allow any date
  const date = new Date(dateStr);
  const checkIn = new Date(stayCheckIn);
  const checkOut = new Date(stayCheckOut);
  return date >= checkIn && date < checkOut;
}

/**
 * Format an ISO date string as a readable day name
 */
function formatDayName(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  // Check if it's today or tomorrow
  if (dateStr === today.toISOString().split('T')[0]) {
    return 'today';
  }
  if (dateStr === tomorrow.toISOString().split('T')[0]) {
    return 'tomorrow';
  }
  
  // Otherwise return day name
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return dayNames[date.getDay()] ?? 'that day';
}

// =============================================================================
// COMPONENT
// =============================================================================

interface AskZaxiaViewProps {
  isActive: boolean;
  guest?: ApiGuest | null;
  onBack?: () => void;
  // Voice command callbacks
  onOpenCalendar?: () => void;
  onOpenBill?: () => void;
  onOpenMessages?: () => void;                    // Phase D1.2: Messages view
  onOpenWeather?: () => void;
  onHome?: () => void;                            // Phase D1.2: Return to grid
  // Phase D1.2: Zone switching
  onSwitchToSidebar?: () => void;
  onSwitchToConcierge?: () => void;
  onSwitchToTv?: () => void;
  onTvChannel?: (channel: string) => void;
  onTvNextChannel?: () => void;                   // Phase D1.2: Next channel
  onTvPrevChannel?: () => void;                   // Phase D1.2: Previous channel
  onTvFullscreen?: () => void;
  onTvExitFullscreen?: () => void;                // Phase D1.2: Exit TV fullscreen
  onTvMute?: () => void;                          // Phase D2.2: Mute TV
  onTvUnmute?: () => void;                        // Phase D2.2: Unmute TV
  onTvOn?: () => void;                            // Phase D2.2: Turn TV on
  onTvOff?: () => void;                           // Phase D2.2: Turn TV off
  isTvFullscreen?: boolean;                       // Phase D1.2: TV context awareness
  // Phase D2.4: Navigate to room service card
  onOpenRoomService?: () => void;
  // Phase D2.5: Navigate to spa card
  onOpenSpa?: () => void;
  // Phase D3.1: Navigate to explore view
  onOpenExplore?: () => void;
  // Booking callback with persistence
  onBookingCreated?: (booking: CreateBookingRequest) => Promise<void>;
  // Skip greeting when opened via spacebar (user wants to speak immediately)
  skipGreeting?: boolean;
  // Phase D4.1 + D4.2: Green Stay
  greenStayEnabled?: boolean;
  onGreenStayEnable?: () => void;
  onGreenStayDisable?: () => void;
  // Phase D4.2: Scheduled preferences
  skipCleaningDays?: string[];
  towelsOnlyDays?: string[];
  stayCheckIn?: string;
  stayCheckOut?: string;
  onSkipCleaningChange?: (days: string[]) => void;
  onTowelsOnlyChange?: (days: string[]) => void;
}

export function AskZaxiaView({
  isActive,
  guest,
  onBack,
  onOpenCalendar,
  onOpenBill,
  onOpenMessages,
  onOpenWeather,
  onHome,
  onSwitchToSidebar,
  onSwitchToConcierge,
  onSwitchToTv,
  onTvChannel,
  onTvNextChannel,
  onTvPrevChannel,
  onTvFullscreen,
  onTvExitFullscreen,
  onTvMute,
  onTvUnmute,
  onTvOn,
  onTvOff,
  isTvFullscreen = false,
  onOpenRoomService,
  onOpenSpa,
  onOpenExplore,
  onBookingCreated,
  skipGreeting = false,
  greenStayEnabled = false,
  onGreenStayEnable,
  onGreenStayDisable,
  // Phase D4.2: Scheduled preferences
  skipCleaningDays = [],
  towelsOnlyDays = [],
  stayCheckIn,
  stayCheckOut,
  onSkipCleaningChange,
  onTowelsOnlyChange,
}: AskZaxiaViewProps) {
  const [state, setState] = useState<ConciergeState>('idle');
  const [response, setResponse] = useState<ConciergeResponse | null>(null);
  const [transcript, setTranscript] = useState('');
  const [focusedCardIndex, setFocusedCardIndex] = useState(0);
  const [bookingFlow, setBookingFlow] = useState<BookingFlowState>({ active: false, step: 'initial' });
  const [greetedTimeWindow, setGreetedTimeWindow] = useState<TimeWindow | null>(null);

  // Phase D1.1: Track if we've spoken in this session to avoid greeting spam
  const [hasSpokenThisSession, setHasSpokenThisSession] = useState(false);

  // Phase 4B: Session memory for contextual follow-ups
  const [sessionMemory, setSessionMemory] = useState<SessionMemory>({
    lastRecommendations: [],
    lastTopic: null,
    awaitingRecommendationAction: false,
  });

  // Phase D4.1: Green Stay onboarding flow
  const [greenStayAsking, setGreenStayAsking] = useState(false);
  const greenStayAskingRef = useRef(false); // Ref to track asking state for callbacks
  // Track local enabled state to handle enable/disable in same session before prop updates
  const greenStayEnabledRef = useRef(greenStayEnabled);

  // Keep refs in sync with state
  useEffect(() => {
    greenStayEnabledRef.current = greenStayEnabled;
  }, [greenStayEnabled]);

  useEffect(() => {
    greenStayAskingRef.current = greenStayAsking;
  }, [greenStayAsking]);

  // Phase 4B: Guest context for smart recommendations
  const guestContext = useGuestContext();

  const recognitionRef = useRef<ReturnType<typeof createSpeechRecognition>>(null);
  const isActiveRef = useRef(isActive);
  const transcriptRef = useRef('');

  // Guest display name
  const guestName = getGuestDisplayName(guest);

  // Keep isActive ref current
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Sync transcript to ref for use in callbacks
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  // ==========================================================================
  // VOICE SYNTHESIS - Using centralized ZaxiaVoiceService
  // ==========================================================================

  const speak = useCallback((text: string, onEnd?: () => void) => {
    console.log('[AskZaxia] Speaking:', text);
    zaxiaVoice.speak(text, {
      onStart: () => {
        console.log('[AskZaxia] Speech onStart fired');
        setState('speaking');
      },
      onEnd: () => {
        console.log('[AskZaxia] Speech onEnd fired');
        if (bookingFlow.active) {
          setState('awaiting-reply');
        } else {
          setState('idle');
        }
        onEnd?.();
      },
      onError: (err) => {
        console.error('[AskZaxia] Speech error:', err);
        setState('idle');
        onEnd?.();
      },
    });
  }, [bookingFlow.active]);

  // ==========================================================================
  // SPEECH RECOGNITION - Push-to-talk
  // ==========================================================================

  const startListening = useCallback(() => {
    if (!isSpeechRecognitionAvailable()) {
      speak("I'm sorry, voice recognition isn't available in this browser.");
      return;
    }

    // Cancel any ongoing speech
    zaxiaVoice.cancel();

    // Cancel any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const recognition = createSpeechRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;

    // Set listening state immediately (optimistically)
    setState('listening');
    setTranscript('');

    recognition.onstart = () => {
      // Recognition has started
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result?.[0]) {
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }
      }

      const currentTranscript = finalTranscript || interimTranscript;
      setTranscript(currentTranscript);
      transcriptRef.current = currentTranscript;
    };

    recognition.onerror = () => {
      setState('idle');
    };

    recognition.onend = () => {
      // Recognition ended naturally
    };

    recognition.start();
  }, [speak]);

  const stopListeningAndProcess = useCallback(() => {
    // Stop recognition if running
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // Ignore errors when stopping
      }
      recognitionRef.current = null;
    }

    const finalTranscript = transcriptRef.current.trim();

    if (!finalTranscript) {
      setState('idle');
      return;
    }

    setState('processing');

    // ========== PHASE D4.1: GREEN STAY YES/NO HANDLING ==========
    // Must be checked BEFORE voice command parsing to intercept yes/no
    // Use ref because this callback captures stale state
    if (greenStayAskingRef.current) {
      const lower = finalTranscript.toLowerCase();

      // Check for "yes" response - more flexible matching
      if (/\b(yes|yeah|yep|sure|please|ok|okay|enable|sounds?\s*good|go\s*ahead|i\s*do|i\s*would)\b/i.test(lower)) {
        greenStayAskingRef.current = false;
        setGreenStayAsking(false);
        greenStayEnabledRef.current = true; // Update ref immediately for same-session consistency
        onGreenStayEnable?.();
        speak("Perfect. Green Stay is now enabled. I'll suggest greener options whenever it makes sense, and you're always in control.");
        return;
      }

      // Check for "no" response - more flexible matching
      if (/\b(no|nope|no\s*thanks?|not?\s*now|maybe\s*later|not\s*interested|i\s*don'?t)\b/i.test(lower)) {
        greenStayAskingRef.current = false;
        setGreenStayAsking(false);
        speak("Of course. If you change your mind later, just let me know. Is there anything else I can help you with?");
        return;
      }

      // If they say something else, exit the asking state and process normally
      greenStayAskingRef.current = false;
      setGreenStayAsking(false);
    }

    // ========== CHECK FOR CANCEL/EXIT IN BOOKING FLOW ==========
    if (bookingFlow.active) {
      const lower = finalTranscript.toLowerCase();

      // Check for cancel command
      if (/\b(cancel|stop|abort|forget it|never\s*mind)\b/i.test(lower)) {
        setBookingFlow({ active: false, step: 'initial' });
        speak("Booking cancelled. Is there anything else I can help you with?");
        return;
      }

      // Check for exit command
      if (/\b(exit|close|go\s*back|leave|goodbye|bye)\b/i.test(lower)) {
        setBookingFlow({ active: false, step: 'initial' });
        zaxiaVoice.cancel();
        onBack?.();
        return;
      }
    }

    // ========== BOOKING FLOW HANDLING ==========
    if (bookingFlow.active) {
      switch (bookingFlow.step) {
        case 'initial': {
          const confirmed = parseConfirmation(finalTranscript);
          if (confirmed === true) {
            // Check what slots we still need
            if (!bookingFlow.time) {
              setBookingFlow(prev => ({ ...prev, step: 'time' }));
              speak(`Perfect! What time would you like to dine, ${guestName}?`);
            } else if (!bookingFlow.partySize) {
              setBookingFlow(prev => ({ ...prev, step: 'party-size' }));
              speak(`${formatTime(bookingFlow.time)}, excellent choice. And how many guests will be dining?`);
            } else {
              // All slots filled - go to review
              setBookingFlow(prev => ({ ...prev, step: 'review' }));
              speak(`A table for ${bookingFlow.partySize} at ${bookingFlow.venue?.title} at ${formatTime(bookingFlow.time)}. Is this correct?`);
            }
          } else if (confirmed === false) {
            setBookingFlow({ active: false, step: 'initial' });
            speak("No problem at all. Is there anything else I can help you with?");
          } else {
            speak("I didn't quite catch that. Would you like me to book a table?");
          }
          break;
        }

        case 'time': {
          const time = parseTime(finalTranscript);
          if (time) {
            if (!bookingFlow.partySize) {
              setBookingFlow(prev => ({ ...prev, step: 'party-size', time }));
              speak(`${formatTime(time)}, excellent choice. And how many guests will be dining?`);
            } else {
              // All slots filled - go to review
              setBookingFlow(prev => ({ ...prev, step: 'review', time }));
              speak(`A table for ${bookingFlow.partySize} at ${bookingFlow.venue?.title} at ${formatTime(time)}. Is this correct?`);
            }
          } else {
            speak("What time works best for you? You can say something like 7 PM or 8 o'clock.");
          }
          break;
        }

        case 'party-size': {
          const size = parsePartySize(finalTranscript);
          if (size) {
            // All slots filled - go to review
            setBookingFlow(prev => ({ ...prev, step: 'review', partySize: size }));
            const { venue, time } = bookingFlow;
            speak(`A table for ${size} at ${venue?.title} at ${formatTime(time!)}. Is this correct?`);
          } else {
            speak("How many people will be joining you?");
          }
          break;
        }

        case 'review': {
          const confirmed = parseConfirmation(finalTranscript);
          if (confirmed === true) {
            // Move to confirm step
            setBookingFlow(prev => ({ ...prev, step: 'confirm' }));
            speak("Should I book it?");
          } else if (confirmed === false) {
            // User wants to make changes - go to edit-choice step
            setBookingFlow(prev => ({ ...prev, step: 'edit-choice' }));
            speak("What would you like to change - the time or the number of guests?");
          } else {
            // Check if they're providing new time or party size directly
            const time = parseTime(finalTranscript);
            const size = parsePartySize(finalTranscript);
            if (time) {
              setBookingFlow(prev => ({ ...prev, time }));
              speak(`Updated to ${formatTime(time)}. A table for ${bookingFlow.partySize} at ${bookingFlow.venue?.title} at ${formatTime(time)}. Is this correct?`);
            } else if (size) {
              setBookingFlow(prev => ({ ...prev, partySize: size }));
              speak(`Updated to ${size} guests. A table for ${size} at ${bookingFlow.venue?.title} at ${formatTime(bookingFlow.time!)}. Is this correct?`);
            } else {
              speak("Is this correct? Say yes to proceed or no to make changes.");
            }
          }
          break;
        }

        case 'edit-choice': {
          // User is choosing what to edit (time or guests)
          const lower = finalTranscript.toLowerCase();
          const wantsTime = /time|when|hour|clock|earlier|later/.test(lower);
          const wantsGuests = /guest|people|person|party|size|many|number/.test(lower);

          // Also check for direct values
          const time = parseTime(finalTranscript);
          const size = parsePartySize(finalTranscript);

          if (time) {
            // User gave a new time directly
            setBookingFlow(prev => ({ ...prev, step: 'review', time }));
            speak(`Updated to ${formatTime(time)}. A table for ${bookingFlow.partySize} at ${bookingFlow.venue?.title} at ${formatTime(time)}. Is this correct?`);
          } else if (size) {
            // User gave a new party size directly
            setBookingFlow(prev => ({ ...prev, step: 'review', partySize: size }));
            speak(`Updated to ${size} guests. A table for ${size} at ${bookingFlow.venue?.title} at ${formatTime(bookingFlow.time!)}. Is this correct?`);
          } else if (wantsGuests) {
            // User wants to change guests
            setBookingFlow(prev => ({ ...prev, step: 'party-size' }));
            speak(`How many guests will be dining, ${guestName}?`);
          } else if (wantsTime) {
            // User wants to change time
            setBookingFlow(prev => ({ ...prev, step: 'time' }));
            speak(`What time would you like, ${guestName}?`);
          } else {
            // Couldn't understand - ask again
            speak("Would you like to change the time or the number of guests?");
          }
          break;
        }

        case 'confirm': {
          const confirmed = parseConfirmation(finalTranscript);
          if (confirmed === true) {
            const { venue, time, partySize } = bookingFlow;
            setBookingFlow(prev => ({ ...prev, step: 'complete' }));

            // Create booking via backend
            if (onBookingCreated && venue && time && partySize) {
              const today = new Date();
              const [hours, minutes] = time.split(':').map(Number);
              const datetime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);

              const bookingRequest: CreateBookingRequest = {
                serviceId: venue.resourceId || 'restaurant-terra',
                serviceName: `Table for ${partySize} at ${venue.title}`,
                category: 'RESTAURANT',
                datetime: datetime.toISOString(),
                durationMinutes: 90,
                price: null,
                location: venue.title,
              };

              onBookingCreated(bookingRequest)
                .then(() => {
                  // SUCCESS - speak confirmation AFTER persistence
                  speak(`Booking confirmed! Your table for ${partySize} at ${venue.title} is set for ${formatTime(time)} tonight. I've sent the details to your room. Is there anything else I can help with, ${guestName}?`, () => {
                    setBookingFlow({ active: false, step: 'initial' });
                  });
                })
                .catch(() => {
                  speak("I'm sorry, there was an issue confirming your booking. Please try again or contact the front desk.");
                  setBookingFlow({ active: false, step: 'initial' });
                });
            } else {
              speak(`Booking confirmed! Your table for ${partySize} at ${venue?.title} is set for ${formatTime(time!)} tonight. I've sent the details to your room.`, () => {
                setBookingFlow({ active: false, step: 'initial' });
              });
            }
          } else if (confirmed === false) {
            // Go back to review
            setBookingFlow(prev => ({ ...prev, step: 'review' }));
            speak("No problem. What would you like to change - the time or the number of guests?");
          } else {
            speak("Should I book it? Say yes to confirm or no to make changes.");
          }
          break;
        }
      }
      return;
    }

    // ========== PHASE 4B: RECOMMENDATION ACTION HANDLING ==========
    // Check if user is responding to recommendations ("yes", "book the restaurant", "the first one")
    if (sessionMemory.awaitingRecommendationAction && sessionMemory.lastRecommendations.length > 0) {
      const lower = finalTranscript.toLowerCase();

      // Check for "something else" / "not interested"
      if (wantsSomethingElse(finalTranscript)) {
        setSessionMemory(prev => ({ ...prev, awaitingRecommendationAction: false }));
        speak("No problem! What else can I help you with?");
        return;
      }

      // Check for simple "yes" / "book it" - use first bookable recommendation
      if (/^\s*(yes|yeah|sure|please|book\s*it|go\s*ahead|sounds?\s*good)\s*$/i.test(lower)) {
        const firstBookable = sessionMemory.lastRecommendations.find(r => r.action === RecommendationAction.BOOK);
        if (firstBookable) {
          // Start booking flow with this recommendation
          const newFlow: BookingFlowState = {
            active: true,
            step: 'time',
            venue: firstBookable,
          };
          setBookingFlow(newFlow);
          setSessionMemory(prev => ({ ...prev, awaitingRecommendationAction: false }));
          speak(`Great! I'll book ${firstBookable.title} for you. What time would you like?`);
          return;
        }
      }

      // Check for specific recommendation selection
      const recIndex = parseRecommendationAction(finalTranscript, sessionMemory.lastRecommendations);
      if (recIndex >= 0) {
        const selectedRec = sessionMemory.lastRecommendations[recIndex]!;
        if (selectedRec.action === RecommendationAction.BOOK) {
          // Start booking flow
          const newFlow: BookingFlowState = {
            active: true,
            step: 'time',
            venue: selectedRec,
          };
          setBookingFlow(newFlow);
          setSessionMemory(prev => ({ ...prev, awaitingRecommendationAction: false }));
          speak(`Excellent choice! I'll book ${selectedRec.title} for you. What time would you prefer?`);
          return;
        } else {
          // Non-bookable item - just provide info
          setSessionMemory(prev => ({ ...prev, awaitingRecommendationAction: false }));
          speak(`${selectedRec.title} - ${selectedRec.reason}. Is there anything else I can help you with?`);
          return;
        }
      }

      // No clear recommendation action - check if it's a new command
      // Fall through to regular voice command parsing
      setSessionMemory(prev => ({ ...prev, awaitingRecommendationAction: false }));
    }

    // ========== VOICE COMMAND PARSING ==========
    const action = parseVoiceCommand(finalTranscript);

    if (!action) {
      speak(`I heard "${finalTranscript}". You can ask me to book a table, open your calendar, or check the weather.`);
      return;
    }

    switch (action.type) {
      case 'OPEN_CALENDAR':
        speak("Opening your calendar.", () => {
          onOpenCalendar?.();
        });
        break;

      case 'OPEN_BILL':
        speak("Here's your bill.", () => {
          onOpenBill?.();
        });
        break;

      case 'WEATHER':
        speak("Let me check the weather for you.", () => {
          onOpenWeather?.();
        });
        break;

      case 'TV_CHANNEL':
        speak(`Switching to ${action.channel}.`, () => {
          onTvChannel?.(action.channel);
        });
        break;

      case 'TV_FULLSCREEN':
        speak("Making the TV full screen.", () => {
          onTvFullscreen?.();
        });
        break;

      // Phase D1.2: New screen-level commands
      case 'OPEN_MESSAGES':
        speak("Opening your messages.", () => {
          onOpenMessages?.();
        });
        break;

      case 'HOME':
        speak("Going home.", () => {
          onHome?.();
        });
        break;

      case 'CLOSE':
        speak("Done.", () => {
          onBack?.();
        });
        break;

      case 'TV_EXIT_FULLSCREEN':
        if (isTvFullscreen) {
          speak("Exiting full screen.", () => {
            onTvExitFullscreen?.();
          });
        } else {
          speak("The TV is not in full screen mode.");
        }
        break;

      case 'TV_NEXT_CHANNEL':
        speak("Next channel.", () => {
          onTvNextChannel?.();
        });
        break;

      case 'TV_PREV_CHANNEL':
        speak("Previous channel.", () => {
          onTvPrevChannel?.();
        });
        break;

      // Phase D2.2: Mute/Unmute TV
      case 'TV_MUTE':
        speak("Muting the TV.", () => {
          onTvMute?.();
        });
        break;

      case 'TV_UNMUTE':
        speak("Unmuting the TV.", () => {
          onTvUnmute?.();
        });
        break;

      // Phase D2.2: TV ON/OFF
      case 'TV_ON':
        speak("Turning the TV on.", () => {
          onTvOn?.();
        });
        break;

      case 'TV_OFF':
        speak("Turning the TV off.", () => {
          onTvOff?.();
        });
        break;

      // Phase D2.3: Show promotions (carousel is always visible on TV screen)
      case 'SHOW_PROMOTIONS':
        speak("Today's special offers are displayed on the TV screen. We have our Spa Escape package, Rooftop Cocktail Hour, a Green Stay discount, and guided city tours.");
        break;

      // Phase D2.4: Room service ordering - navigate to Room Service card
      case 'ROOM_SERVICE':
        speak("Opening room service menu.", () => {
          onOpenRoomService?.();
        });
        break;

      // Phase D2.5: Spa booking - navigate to Spa card
      case 'SPA':
        speak("Opening the spa. Let me show you our treatments.", () => {
          onOpenSpa?.();
        });
        break;

      // Phase D3.1: Explore nearby - navigate to Explore view
      case 'EXPLORE':
        speak("Let me show you what's nearby.", () => {
          onOpenExplore?.();
        });
        break;

      // Phase D4.1: Green Stay voice onboarding
      case 'GREEN_STAY_ASK': {
        // Explain Green Stay and ask if they want to enable it - use ref for immediate state check
        const isEnabled = greenStayEnabledRef.current;
        const explanation = isEnabled
          ? "Green Stay is already enabled for your stay. I'll continue suggesting greener options when it makes sense."
          : "Green Stay lets you enjoy your stay while reducing unnecessary waste. For example, skipping daily cleaning can save around 60 liters of water per day. Would you like me to enable Green Stay for your stay?";
        const shouldAsk = !isEnabled;
        greenStayAskingRef.current = shouldAsk; // Update ref immediately for callback
        setGreenStayAsking(shouldAsk);
        speak(explanation);
        break;
      }

      case 'GREEN_STAY_ENABLE': {
        // Direct enable command - use ref for immediate state check
        if (greenStayEnabledRef.current) {
          speak("Green Stay is already enabled for your stay.");
        } else {
          greenStayEnabledRef.current = true; // Update ref immediately
          greenStayAskingRef.current = false;
          onGreenStayEnable?.();
          setGreenStayAsking(false);
          speak("Perfect. Green Stay is now enabled. I'll suggest greener options whenever it makes sense, and you're always in control.");
        }
        break;
      }

      case 'GREEN_STAY_DISABLE': {
        // Direct disable command - use ref for immediate state check
        if (!greenStayEnabledRef.current) {
          speak("Green Stay is not currently enabled.");
        } else {
          greenStayEnabledRef.current = false; // Update ref immediately
          // Check if there are scheduled preferences to cancel
          const hasScheduledPrefs = skipCleaningDays.length > 0 || towelsOnlyDays.length > 0;
          // Clear all scheduled preferences when disabling
          onSkipCleaningChange?.([]);
          onTowelsOnlyChange?.([]);
          onGreenStayDisable?.();
          if (hasScheduledPrefs) {
            speak("Green Stay has been disabled and your scheduled cleaning preferences have been canceled. Full room service will resume. You can enable it again anytime by asking me.");
          } else {
            speak("Green Stay has been disabled. You can enable it again anytime by asking me.");
          }
        }
        break;
      }

      // Phase D4.2: Scheduled Green Stay preferences
      case 'GREEN_STAY_SKIP_CLEANING': {
        const targetDate = parseDayReference(action.day, stayCheckIn, stayCheckOut);
        if (!targetDate) {
          speak("I couldn't understand which day you mean. Please try again with a specific day like tomorrow or Monday.");
          break;
        }
        if (!isDateInStayRange(targetDate, stayCheckIn, stayCheckOut)) {
          speak("That date is outside your stay. Please choose a day during your visit.");
          break;
        }
        // Auto-enable Eco Mode if not already enabled
        if (!greenStayEnabledRef.current) {
          greenStayEnabledRef.current = true;
          onGreenStayEnable?.();
        }
        const newDays = skipCleaningDays.includes(targetDate)
          ? skipCleaningDays
          : [...skipCleaningDays, targetDate];
        onSkipCleaningChange?.(newDays);
        const dayName = formatDayName(targetDate);
        speak(`Got it. I've scheduled to skip cleaning on ${dayName}. The housekeeping team will be notified.`);
        break;
      }

      case 'GREEN_STAY_TOWELS_ONLY': {
        const targetDate = parseDayReference(action.day, stayCheckIn, stayCheckOut);
        if (!targetDate) {
          speak("I couldn't understand which day you mean. Please try again with a specific day like tomorrow or Thursday.");
          break;
        }
        if (!isDateInStayRange(targetDate, stayCheckIn, stayCheckOut)) {
          speak("That date is outside your stay. Please choose a day during your visit.");
          break;
        }
        // Auto-enable Eco Mode if not already enabled
        if (!greenStayEnabledRef.current) {
          greenStayEnabledRef.current = true;
          onGreenStayEnable?.();
        }
        const newDays = towelsOnlyDays.includes(targetDate)
          ? towelsOnlyDays
          : [...towelsOnlyDays, targetDate];
        onTowelsOnlyChange?.(newDays);
        const dayName = formatDayName(targetDate);
        speak(`Got it. I've scheduled towels only on ${dayName}. Fresh towels will be provided without full cleaning.`);
        break;
      }

      case 'GREEN_STAY_CLEAN_ROOM': {
        const targetDate = parseDayReference(action.day, stayCheckIn, stayCheckOut);
        if (!targetDate) {
          speak("I couldn't understand which day you mean. Please try again with a specific day.");
          break;
        }
        // Remove from both skip cleaning and towels only
        const newSkipDays = skipCleaningDays.filter(d => d !== targetDate);
        const newTowelsDays = towelsOnlyDays.filter(d => d !== targetDate);
        if (newSkipDays.length !== skipCleaningDays.length || newTowelsDays.length !== towelsOnlyDays.length) {
          onSkipCleaningChange?.(newSkipDays);
          onTowelsOnlyChange?.(newTowelsDays);
          const dayName = formatDayName(targetDate);
          speak(`Got it. I've scheduled a full room cleaning on ${dayName}.`);
        } else {
          const dayName = formatDayName(targetDate);
          speak(`Full room cleaning is already scheduled for ${dayName}.`);
        }
        break;
      }

      case 'GREEN_STAY_SHOW': {
        const skipCount = skipCleaningDays.length;
        const towelsCount = towelsOnlyDays.length;
        if (skipCount === 0 && towelsCount === 0) {
          speak("You don't have any green stay preferences scheduled yet. You can say things like 'skip cleaning tomorrow' or 'only towels on Thursday'.");
        } else {
          let response = "Here are your green stay preferences: ";
          if (skipCount > 0) {
            const skipDayNames = skipCleaningDays.map(formatDayName).join(', ');
            response += `Skip cleaning on ${skipDayNames}. `;
          }
          if (towelsCount > 0) {
            const towelDayNames = towelsOnlyDays.map(formatDayName).join(', ');
            response += `Towels only on ${towelDayNames}.`;
          }
          speak(response);
        }
        break;
      }

      case 'GREEN_STAY_CLEAR': {
        if (skipCleaningDays.length === 0 && towelsOnlyDays.length === 0) {
          speak("You don't have any green stay preferences to clear.");
        } else {
          onSkipCleaningChange?.([]);
          onTowelsOnlyChange?.([]);
          speak("I've cleared all your green stay preferences. Full room cleaning is now scheduled for every day of your stay.");
        }
        break;
      }

      // Phase D4.3: Transport request with eco-aware suggestions
      case 'TRANSPORT_REQUEST': {
        if (greenStayEnabledRef.current) {
          // Eco-aware transport suggestion when Green Stay is ON
          speak("Since you're on Green Stay, walking would be the greenest option — many destinations are just 10-15 minutes away. The hotel also offers bicycles free of charge. Would you still prefer a taxi?");
        } else {
          // Standard transport response when Green Stay is OFF
          speak("I can arrange a taxi for you. Where would you like to go?");
        }
        break;
      }

      // Phase D4.3: Housekeeping request with eco-aware response
      case 'HOUSEKEEPING_REQUEST': {
        if (greenStayEnabledRef.current) {
          // Eco-aware housekeeping suggestion when Green Stay is ON
          const todayStr = new Date().toISOString().split('T')[0]!;
          const isSkippingToday = skipCleaningDays.includes(todayStr);
          const isTowelsOnlyToday = towelsOnlyDays.includes(todayStr);
          
          if (isSkippingToday) {
            speak("You're currently on Green Stay with cleaning skipped for today. Would you like me to schedule a full room cleaning instead? It helps save resources, but I'm happy to arrange cleaning if you need it.");
          } else if (isTowelsOnlyToday) {
            speak("You're on Green Stay with towels-only service today. Would you like me to upgrade to a full room cleaning? I can do that while keeping your Green Stay preferences for other days.");
          } else {
            speak("I can request housekeeping for you. Since you're on Green Stay, would you prefer a light refresh or a full cleaning?");
          }
        } else {
          // Standard housekeeping response when Green Stay is OFF
          speak("I'll notify housekeeping to clean your room. They typically arrive within 30 minutes. Is that okay?");
        }
        break;
      }

      // Phase D4.3: Laundry request with eco-aware response
      case 'LAUNDRY_REQUEST': {
        if (greenStayEnabledRef.current) {
          // Eco-aware laundry suggestion when Green Stay is ON
          speak("Since you're on Green Stay, our eco-friendly laundry service uses cold water and plant-based detergents. Items are usually ready within 24 hours. Would you like me to arrange a pickup?");
        } else {
          // Standard laundry response when Green Stay is OFF
          speak("I can arrange laundry service for you. We offer same-day service for items dropped off before 10 AM, or next-day service. Would you like me to request a pickup?");
        }
        break;
      }

      // Phase D1.2: Zone switching
      case 'ZONE_SIDEBAR':
        speak("Switching to sidebar.", () => {
          onSwitchToSidebar?.();
        });
        break;

      case 'ZONE_CONCIERGE':
        speak("Switching to concierge.", () => {
          onSwitchToConcierge?.();
        });
        break;

      case 'ZONE_TV':
        speak("Switching to TV.", () => {
          onSwitchToTv?.();
        });
        break;

      case 'CANCEL_BOOKING':
        if (bookingFlow.active) {
          setBookingFlow({ active: false, step: 'initial' });
          speak("Booking cancelled. Is there anything else I can help you with?");
        } else {
          speak("There's no active booking to cancel. How can I help you?");
        }
        break;

      case 'EXIT':
        speak("Goodbye!", () => {
          onBack?.();
        });
        break;

      case 'BOOK_RESTAURANT': {
        const { time, partySize } = action.slots;

        // Fetch restaurant recommendations
        askConcierge(ConciergeIntent.IM_HUNGRY, new Date())
          .then(result => {
            setResponse(result);
            setFocusedCardIndex(0);

            const topVenue = result.recommendations.find(r => r.type === RecommendationType.VENUE);
            if (topVenue) {
              // Pre-fill slots from single utterance
              const newFlow: BookingFlowState = {
                active: true,
                step: 'initial',
                venue: topVenue,
                time: time,
                partySize: partySize,
              };
              setBookingFlow(newFlow);

              // Build response based on extracted slots
              if (time && partySize) {
                // All slots filled - go to review step ("Is this correct?")
                setBookingFlow({ ...newFlow, step: 'review' });
                speak(`A table for ${partySize} at ${topVenue.title} at ${formatTime(time)}. Is this correct?`);
              } else if (time) {
                // Have time, need party size
                setBookingFlow({ ...newFlow, step: 'party-size' });
                speak(`${topVenue.title} has availability at ${formatTime(time)}. How many guests will be dining?`);
              } else if (partySize) {
                // Have party size, need time
                setBookingFlow({ ...newFlow, step: 'time' });
                speak(`${topVenue.title} can accommodate ${partySize} guests. What time would you like to dine?`);
              } else {
                // No slots - ask to book
                speak(`${result.spokenResponse} Would you like me to book a table at ${topVenue.title}?`);
              }
            } else {
              speak(result.spokenResponse);
            }
          })
          .catch(() => {
            speak(`I'm sorry ${guestName}, I had trouble with that request. Could you try again?`);
          });
        break;
      }

      case 'CONCIERGE_INTENT':
        // Phase 4B: Use guest context for smarter recommendations
        console.log(`[AskZaxia] CONCIERGE_INTENT: isCheckoutDay=${guestContext?.isCheckoutDay}, isFirstDay=${guestContext?.isFirstDay}, stayDay=${guestContext?.stayDay}`);
        askConcierge(action.intent, new Date(), guestContext ? {
          guestName: guestContext.guestNames.split(' ')[0],
          stayDay: guestContext.stayDay,
          isCheckoutDay: guestContext.isCheckoutDay,
          isFirstDay: guestContext.isFirstDay,
          todayBookingServiceIds: guestContext.todayBookings.map(b => b.serviceId),
          timeOfDay: guestContext.timeOfDay,
        } : undefined)
          .then(result => {
            setResponse(result);
            setFocusedCardIndex(0);

            // Phase 4B: Store in session memory for follow-up actions
            setSessionMemory({
              lastRecommendations: result.recommendations,
              lastTopic: action.intent,
              awaitingRecommendationAction: true,
            });

            // Phase 4B: Natural spoken response with booking offer
            const bookableRecs = result.recommendations.filter(r => r.action === RecommendationAction.BOOK);
            if (bookableRecs.length > 0) {
              speak(`${result.spokenResponse} Would you like me to book any of these for you?`);
            } else {
              speak(result.spokenResponse);
            }
          })
          .catch(() => {
            speak(`I'm sorry ${guestName}, I had trouble with that request. Could you try again?`);
          });
        break;
    }
  }, [bookingFlow, sessionMemory, speak, guestName, guestContext, onBookingCreated, onOpenCalendar, onOpenBill, onOpenMessages, onOpenWeather, onHome, onTvChannel, onTvNextChannel, onTvPrevChannel, onTvFullscreen, onTvExitFullscreen, onTvMute, onTvUnmute, onTvOn, onTvOff, isTvFullscreen, onBack, onSwitchToSidebar, onSwitchToConcierge, onSwitchToTv]);

  // ==========================================================================
  // SPACEBAR PUSH-TO-TALK - Internal handling when view is active
  // ==========================================================================

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;

      // Don't trigger if user is typing in an input/textarea
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      e.preventDefault();
      e.stopPropagation();

      // Start listening on spacebar press
      startListening();
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;

      e.preventDefault();
      e.stopPropagation();

      // Stop listening and process on spacebar release
      stopListeningAndProcess();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [isActive, startListening, stopListeningAndProcess]);

  // ==========================================================================
  // ACTION HANDLERS
  // ==========================================================================

  const handleCardAction = useCallback((card: Recommendation) => {
    if (card.action === RecommendationAction.BOOK && card.type === RecommendationType.VENUE) {
      setBookingFlow({ active: true, step: 'time', venue: card });
      speak(`Great choice! What time would you like your table at ${card.title}?`);
    } else {
      speak(`I'll pull up more information about ${card.title}.`);
    }
  }, [speak]);

  const handleReset = useCallback(() => {
    zaxiaVoice.cancel();
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setState('idle');
    setResponse(null);
    setTranscript('');
    setFocusedCardIndex(0);
    setBookingFlow({ active: false, step: 'initial' });
  }, []);

  // ==========================================================================
  // KEYBOARD HANDLERS - Arrow keys and Escape (Space is handled globally)
  // ==========================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActiveRef.current) return;
      if (e.defaultPrevented) return;

      // Arrow keys for card navigation
      if (state === 'idle' && response?.recommendations.length) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          setFocusedCardIndex(prev => Math.max(0, prev - 1));
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          setFocusedCardIndex(prev => Math.min(response.recommendations.length - 1, prev + 1));
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const card = response.recommendations[focusedCardIndex];
          if (card) handleCardAction(card);
        }
      }

      // Escape to go back
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        zaxiaVoice.cancel();
        if (recognitionRef.current) {
          recognitionRef.current.abort();
        }
        if (bookingFlow.active) {
          setBookingFlow({ active: false, step: 'initial' });
          speak("Booking cancelled. How else can I help?");
        } else if (response) {
          handleReset();
        } else if (onBack) {
          onBack();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [state, response, focusedCardIndex, bookingFlow, speak, onBack, handleReset, handleCardAction]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      zaxiaVoice.cancel();
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // ==========================================================================
  // GREETING - Once per time window, silent on subsequent activations
  // ==========================================================================

  useEffect(() => {
    // Skip greeting if opened via spacebar (user wants to speak immediately)
    if (skipGreeting) return;

    if (isActive) {
      const currentWindow = getCurrentTimeWindow();

      if (greetedTimeWindow !== currentWindow) {
        // First greeting of this time window - full greeting
        const timer = setTimeout(() => {
          const greeting = getGreeting(currentWindow);
          speak(`${greeting}, ${guestName}. How can I help you?`);
          setGreetedTimeWindow(currentWindow);
          setHasSpokenThisSession(true);
        }, 500);
        return () => clearTimeout(timer);
      } else if (!hasSpokenThisSession) {
        // Same time window, but first open this session - brief prompt
        const timer = setTimeout(() => {
          speak("How can I help?");
          setHasSpokenThisSession(true);
        }, 500);
        return () => clearTimeout(timer);
      }
      // Otherwise: silent - user just closed and reopened
    }
  }, [isActive, guestName, speak, greetedTimeWindow, skipGreeting, hasSpokenThisSession]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className="ask-zaxia-view">
      {/* Header */}
      <div className="ask-zaxia-header">
        {onBack && (
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        )}
        <div className="header-title">
          <span className="zaxia-logo">Z</span>
          <span className="zaxia-name">Zaxia</span>
        </div>
        {response && (
          <button className="reset-button" onClick={handleReset}>
            New question
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="ask-zaxia-content">
        <AnimatePresence mode="wait">

          {/* Voice Visualization */}
          <motion.div
            key="voice-area"
            className="voice-area"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Mic indicator - clickable for push-to-talk */}
            <div
              className={`mic-orb ${state}`}
              onMouseDown={(e) => {
                e.preventDefault();
                if (state !== 'speaking' && state !== 'processing') {
                  startListening();
                }
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                if (state === 'listening') {
                  stopListeningAndProcess();
                }
              }}
              onMouseLeave={() => {
                // If user drags mouse away while holding, stop listening
                if (state === 'listening') {
                  stopListeningAndProcess();
                }
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                if (state !== 'speaking' && state !== 'processing') {
                  startListening();
                }
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                if (state === 'listening') {
                  stopListeningAndProcess();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Hold to speak"
            >
              <div className="orb-inner">
                {state === 'listening' && <div className="listening-ring" />}
                {state === 'listening' && <div className="listening-ring delay" />}
                {state === 'processing' && <div className="processing-spinner" />}
                <span className="mic-icon">
                  {state === 'listening' ? '🎤' : state === 'speaking' ? '💬' : state === 'processing' ? '⏳' : '🎤'}
                </span>
              </div>
            </div>

            {/* Status text */}
            <p className="status-text">
              {state === 'listening' && (transcript || 'Listening...')}
              {state === 'speaking' && 'Zaxia is speaking...'}
              {state === 'processing' && 'Processing...'}
              {state === 'awaiting-reply' && 'Hold mic to respond...'}
              {state === 'idle' && !response && 'Hold the mic to speak'}
              {state === 'idle' && response && 'Hold the mic to ask another question'}
            </p>

            {/* Interaction hint */}
            <p className="keyboard-hint">
              Press and hold the mic button &nbsp;•&nbsp; <kbd>Esc</kbd> Go back
            </p>
          </motion.div>

          {/* Response Section */}
          {response && state !== 'listening' && (
            <motion.div
              key="response"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="response-section"
            >
              {/* Recommendation Cards */}
              {response.recommendations.length > 0 && (
                <div className="recommendations-grid">
                  {response.recommendations.map((rec, index) => (
                    <motion.div
                      key={rec.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className={`recommendation-card type-${rec.type.toLowerCase()} ${focusedCardIndex === index ? 'focused' : ''}`}
                      onClick={() => handleCardAction(rec)}
                    >
                      <div className="card-header">
                        <span className="type-icon">{TYPE_ICONS[rec.type]}</span>
                        <span className="type-label">{rec.type}</span>
                      </div>
                      <h3 className="card-title">{rec.title}</h3>
                      {rec.subtitle && <p className="card-subtitle">{rec.subtitle}</p>}
                      <p className="card-reason">{rec.reason}</p>
                      {rec.price !== undefined && (
                        <p className="card-price">{rec.currency || '€'}{rec.price}</p>
                      )}
                      <button className="card-action">
                        {rec.action === RecommendationAction.BOOK ? 'Book now' : 'View details'}
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Booking Flow Indicator */}
          {bookingFlow.active && (
            <motion.div
              key="booking-flow"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="booking-flow-indicator"
            >
              <div className="booking-progress">
                <span className={bookingFlow.step === 'time' ? 'active' : bookingFlow.time ? 'complete' : ''}>Time</span>
                <span className="divider">→</span>
                <span className={bookingFlow.step === 'party-size' ? 'active' : bookingFlow.partySize ? 'complete' : ''}>Guests</span>
                <span className="divider">→</span>
                <span className={bookingFlow.step === 'review' || bookingFlow.step === 'edit-choice' ? 'active' : (bookingFlow.step === 'confirm' || bookingFlow.step === 'complete') ? 'complete' : ''}>Review</span>
                <span className="divider">→</span>
                <span className={bookingFlow.step === 'confirm' || bookingFlow.step === 'complete' ? 'active' : ''}>Confirm</span>
              </div>
              {bookingFlow.venue && (
                <p className="booking-venue">Booking at {bookingFlow.venue.title}</p>
              )}
              {/* Pre-filled slots display */}
              <div className="booking-slots">
                {bookingFlow.time && <span className="slot">🕐 {formatTime(bookingFlow.time)}</span>}
                {bookingFlow.partySize && <span className="slot">👥 {bookingFlow.partySize} guests</span>}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      <style>{`
        .ask-zaxia-view {
          position: relative;
          display: flex;
          flex-direction: column;
          height: 100%;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          color: #fff;
          overflow: hidden;
        }

        .ask-zaxia-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .back-button, .reset-button {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: #fff;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: background 0.2s;
        }

        .back-button:hover, .reset-button:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .header-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .zaxia-logo {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 1.5rem;
        }

        .zaxia-name {
          font-size: 1.5rem;
          font-weight: 600;
        }

        .ask-zaxia-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 2rem;
          overflow-y: auto;
          gap: 2rem;
        }

        /* Voice Area */
        .voice-area {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
        }

        .mic-orb {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          transition: transform 0.3s, box-shadow 0.3s;
          box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }

        .mic-orb:hover {
          transform: scale(1.05);
          box-shadow: 0 10px 50px rgba(102, 126, 234, 0.4);
        }

        .mic-orb:active,
        .mic-orb.listening {
          transform: scale(1.1);
          box-shadow: 0 10px 60px rgba(102, 126, 234, 0.5);
        }

        .mic-orb.processing {
          animation: pulse-process 0.8s ease-in-out infinite;
        }

        .mic-orb.speaking {
          animation: pulse-speak 1s ease-in-out infinite;
        }

        @keyframes pulse-process {
          0%, 100% { transform: scale(1); box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3); }
          50% { transform: scale(1.03); box-shadow: 0 10px 50px rgba(102, 126, 234, 0.4); }
        }

        @keyframes pulse-speak {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .processing-spinner {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 3px solid transparent;
          border-top-color: rgba(255, 255, 255, 0.8);
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .orb-inner {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .listening-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 3px solid rgba(255, 255, 255, 0.5);
          animation: ring-expand 1.5s ease-out infinite;
        }

        .listening-ring.delay {
          animation-delay: 0.5s;
        }

        @keyframes ring-expand {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.6); opacity: 0; }
        }

        .mic-icon {
          font-size: 3rem;
          z-index: 1;
        }

        .status-text {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          text-align: center;
          min-height: 2rem;
        }

        .keyboard-hint {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.5);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .keyboard-hint kbd {
          background: rgba(255, 255, 255, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 4px;
          padding: 0.2rem 0.5rem;
          font-family: inherit;
          font-size: 0.8rem;
        }

        /* Response Section */
        .response-section {
          width: 100%;
          max-width: 900px;
        }

        /* Recommendation Cards */
        .recommendations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1rem;
        }

        .recommendation-card {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          border: 2px solid rgba(255, 255, 255, 0.1);
          transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
          cursor: pointer;
        }

        .recommendation-card:hover,
        .recommendation-card.focused {
          transform: translateY(-4px);
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.6);
        }

        .type-icon { font-size: 1.1rem; }

        .card-title {
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0;
        }

        .card-subtitle {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }

        .card-reason {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
          flex: 1;
        }

        .card-price {
          font-size: 1.1rem;
          font-weight: 600;
          color: #667eea;
          margin: 0;
        }

        .card-action {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          color: #fff;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: opacity 0.2s;
        }

        .card-action:hover { opacity: 0.9; }

        /* Type-specific card colors */
        .recommendation-card.type-venue { border-left: 3px solid #f97316; }
        .recommendation-card.type-spa { border-left: 3px solid #a855f7; }
        .recommendation-card.type-activity { border-left: 3px solid #22c55e; }
        .recommendation-card.type-local { border-left: 3px solid #3b82f6; }

        /* Booking Flow */
        .booking-flow-indicator {
          background: rgba(102, 126, 234, 0.15);
          border: 1px solid rgba(102, 126, 234, 0.3);
          border-radius: 12px;
          padding: 1rem 1.5rem;
          text-align: center;
        }

        .booking-progress {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.5);
        }

        .booking-progress span.active {
          color: #667eea;
          font-weight: 600;
        }

        .booking-progress span.complete {
          color: #22c55e;
        }

        .booking-progress .divider {
          color: rgba(255, 255, 255, 0.3);
        }

        .booking-venue {
          margin: 0.5rem 0 0;
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.7);
        }

        .booking-slots {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 0.5rem;
        }

        .booking-slots .slot {
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid rgba(34, 197, 94, 0.4);
          border-radius: 6px;
          padding: 0.25rem 0.75rem;
          font-size: 0.85rem;
          color: #22c55e;
        }
      `}</style>
    </div>
  );
}

export default AskZaxiaView;
